import React from 'react';
import { Typography, TextField, Theme, Box, Grid } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { CurrencyInfo } from 'common/types';
import countryList from 'common/services/countryCodes.json';
import { HOP_CURRENCY_CODES, HopCurrencyCode } from 'common/constants/currencies';
import { currencyOptions as currencies } from 'common/utils/currencyUtils';
import { ADYEN_HOP_COUNTRY_CODES } from 'common/constants/countries';
import { useTranslation } from 'services/localization/useTranslation';
import { FormState, Controller, Control } from 'react-hook-form';
import { OTHER_COUNTRY, OTHER_CURRENCY, PaymentFormValues } from './index';

interface HopCurrencyInfo extends Omit<CurrencyInfo, 'code'> {
	code: HopCurrencyCode;
}

interface Props {
	control: Control<PaymentFormValues, any>;
	formState: FormState<PaymentFormValues>;
	formSendError?: string;
}

export const PaymentInfoForm = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { control, formState, formSendError } = props;
	const supportedCountryList = countryList.filter((countryObj) =>
		ADYEN_HOP_COUNTRY_CODES.some((code) => code === countryObj.code),
	);
	const hopCurrenciesList: HopCurrencyInfo[] = currencies.filter((currency) =>
		HOP_CURRENCY_CODES.some((code) => code === currency.code),
	) as HopCurrencyInfo[];

	const { errors } = formState;

	return (
		<Box>
			<Typography variant="body1">
				{t('account.payments.completeDetailsToContinue', 'Complete your details to continue')}
			</Typography>
			<Box mb={2} />
			<Grid container spacing={2}>
				<Grid item xs={12} sm={6}>
					<Controller
						control={control}
						name="legalName"
						render={(renderProps) => (
							<TextField
								label={t('common:form.businessName', 'Business name')}
								margin="normal"
								type="text"
								className={classes.textField}
								value={renderProps.field.value}
								onChange={(e) => renderProps.field.onChange(e.target.value)}
								error={!!errors.legalName?.message}
								helperText={
									errors.legalName?.message ||
									t('common:form.businessNameHelper', 'The legal name of your business')
								}
								variant="outlined"
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						control={control}
						name="email"
						render={(renderProps) => (
							<TextField
								label={t('common:form.email')}
								className={classes.textField}
								value={renderProps.field.value}
								onChange={(e) => renderProps.field.onChange(e.target.value)}
								margin="normal"
								type="email"
								error={!!errors.email?.message}
								helperText={errors.email?.message}
								variant="outlined"
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						control={control}
						name="country"
						render={(renderProps) => (
							<TextField
								select
								label={t('common:address.country', 'Country')}
								className={classes.textField}
								value={renderProps.field.value}
								onChange={(e) => renderProps.field.onChange(e.target.value)}
								SelectProps={{
									native: true,
									MenuProps: {
										className: classes.menu,
									},
								}}
								InputLabelProps={{
									shrink: true,
								}}
								margin="normal"
								error={!!errors.country?.message}
								helperText={errors.country?.message}
								variant="outlined"
							>
								<option value={''} disabled>
									{t('shop.selectCountry', 'Select country')}
								</option>
								{supportedCountryList.map((countryObj) => (
									<option key={countryObj.code} value={countryObj.code}>
										{countryObj.flag} {countryObj.name}
									</option>
								))}
								<option value={OTHER_COUNTRY}>{t('common:form.other', 'Other')}</option>
							</TextField>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						control={control}
						name="currency"
						render={(renderProps) => (
							<TextField
								select
								label={t('common:payment.currency')}
								className={classes.textField}
								value={renderProps.field.value}
								onChange={(e) =>
									renderProps.field.onChange(
										e.target.value as HopCurrencyCode | typeof OTHER_CURRENCY,
									)
								}
								SelectProps={{
									native: true,
									MenuProps: {
										className: classes.menu,
									},
								}}
								InputLabelProps={{
									shrink: true,
								}}
								margin="normal"
								variant="outlined"
							>
								<option value={''} disabled>
									{t('shop.selectCurrency', 'Select currency')}
								</option>
								{hopCurrenciesList.map((currency) => (
									<option key={currency.code} value={currency.code}>
										{`${currency.code} (${currency.name})`}
									</option>
								))}
								<option value={OTHER_CURRENCY}>{t('common:form.other', 'Other')}</option>
							</TextField>
						)}
					/>
				</Grid>
			</Grid>
			<Typography variant="body1" color="error" gutterBottom>
				{formSendError}
			</Typography>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		textField: {
			marginLeft: theme.spacing(1),
			marginRight: theme.spacing(1),
			maxWidth: 380,
			width: `calc(100% - ${theme.spacing(2)})`,
		},
		menu: {
			width: 200,
		},
	}),
);
